import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { ArticleComponentDispatcher } from '@/src/components/feature/article';
import Link from 'next/link';
import { ArticleBody } from '@/constants/article';
import FunwooAPI, { GetPublishedPostResponseEntity, ListingListEntity, ListingStatusEnum } from '@/swagger';
import Listing from '@/src/components/UI/Listing';
import { useForm } from 'react-hook-form';
import { emailRequired } from '@/utils/fieldValidator';
import { useRouter } from 'next/router';
import { CloseButton, Flex, Modal, ModalBody, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { sleep } from '@/utils/sleep';
import ReactIcon from '@/src/components/common/ReactIcon';

const getPostUrl = (prefix: string, uuid: string) => `/${prefix}/${encodeURIComponent(uuid)}`;

const ArticleReader: React.FC<{
  linkPrefix: string;
  body: Array<ArticleBody>;
  recommendPosts: GetPublishedPostResponseEntity;
  relatedListings: Array<ListingListEntity>;
}> = ({ body, recommendPosts, linkPrefix, relatedListings }) => {
  const router = useRouter();

  useEffect(() => {
    recommendPosts.items.forEach((item) => {
      if (item.foreignUrl) return;
      router.prefetch(getPostUrl(linkPrefix, item.uuid));
    });
  }, [linkPrefix, recommendPosts]);
  useEffect(() => {
    relatedListings.forEach((listing) => {
      `/buy/${listing.sid}`;
    });
  }, [linkPrefix, relatedListings]);

  return (
    <React.Fragment>
      <div className={classNames('sm:divide-y', 'sm:divide-gray300')}>
        <div className={classNames('max-w-[1200px] mx-2 md:mx-auto', '[&>*:first-child]:mt-0', 'sm:pt-14 pb-8')}>
          {body.map((item, index) => (
            <ArticleComponentDispatcher body={item} key={index} />
          ))}
        </div>
        <div className={classNames('max-w-[1200px]', 'mx-2 md:mx-auto', 'sm:py-8')}>
          <div className={classNames('text-lg')}>
            <p className={classNames('font-medium sm:font-semibold')}>延伸閱讀</p>
            <div>
              {recommendPosts.items.map((item) => (
                <Link href={item.foreignUrl || getPostUrl(linkPrefix, item.uuid)} key={item.id} passHref>
                  <a className={classNames('block', 'underline', 'text-[#003D8C]')}>{item.title}</a>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className={classNames('max-w-[1200px]', 'mx-2 md:mx-auto', 'sm:py-8')}>
          <EDMSubscriber />
        </div>
        <div className={classNames('max-w-[1200px] mx-2 md:mx-auto', 'pt-4 space-y-6 sm:pt-8')}>
          <p className={'text-lg font-medium sm:font-semibold'}>您可能有興趣的房源</p>
          <div
            className={classNames(
              'flex flex-col md:flex-row items-start md:items-stretch',
              'max-w-full',
              'space-y-6 md:space-y-0 md:space-x-6'
            )}
          >
            {relatedListings
              .filter((listing) => listing.status === ListingStatusEnum.Active)
              ?.map((listing) => (
                <div
                  key={listing.id}
                  className={classNames(
                    'sm:flex-1',
                    'max-w-full w-full md:w-auto',
                    'sm:overflow-hidden',
                    'rounded',
                    'shadow-[2px_2px_4px_0_rgba(0,0,0,0.04),_2px_2px_8px_0_rgba(0,0,0,0.04)]',
                    'hover:shadow-[-2px_-2px_8px_rgba(0,0,0,0.12),_2px_2px_8px_rgba(0,0,0,0.12)]'
                  )}
                >
                  <Listing listing={listing} sizes={'100%'} className={'h-full'} />
                </div>
              ))}
            {Array.from({ length: 3 - relatedListings?.length }).map((_, index) => (
              <div
                key={index}
                className={classNames(
                  'hidden invisible sm:block sm:visible',
                  'sm:flex-1',
                  'max-w-full w-full md:w-auto'
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ArticleReader;

const EDMSubscriber: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{
    email: string;
  }>();

  const onSubmit = useCallback(
    handleSubmit(async ({ email }) => {
      await FunwooAPI.edmApi.subscribe({ name: 'subscriber_from_website', email });
      onOpen();
      reset();
      await sleep(3000);
      onClose();
    }),
    []
  );

  return (
    <React.Fragment>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay px={8} />
        <ModalContent
          w={'full'}
          maxW={`37.5rem`}
          borderRadius={0}
          containerProps={{
            px: 8,
            alignItems: 'center',
          }}
        >
          <ModalBody w={'full'} position={'relative'}>
            <CloseButton position={'absolute'} right={6} top={3} />
            <Flex
              alignItems={'center'}
              justifyContent={'center'}
              flexDirection={'column'}
              experimental_spaceY={{
                base: 6,
              }}
              py={10}
            >
              <ReactIcon icon={AiFillCheckCircle} className={'text-gold'} size={72} />
              <p className={'text-headline2 text-gold font-medium'}>感謝您的訂閱</p>
              <p className={classNames('text-center', 'text-base', 'max-w-[22.5rem]')}>
                FUNWOO 將每月定期發送美國第一手趨勢報告，讓您即時掌握最新房產動態！
              </p>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <form className={classNames('pt-4 sm:pt-0', 'space-y-2 sm:space-y-0')} onSubmit={onSubmit}>
        <div className={classNames('w-full h-2.5', 'bg-gray900', 'sm:hidden sm:invisible')} />
        <div
          className={classNames(
            'sm:flex sm:items-end',
            'pt-2 pb-6 px-4 sm:py-0 sm:px-0',
            'space-y-4 sm:space-y-0',
            'bg-gray50 sm:bg-transparent'
          )}
        >
          <div className={classNames('pb-2 space-y-2 sm:space-y-0 sm:pb-0', 'sm:flex-1')}>
            <p className={classNames('text-headline4 sm:text-lg font-medium sm:font-semibold')}>訂閱 FUNWOO 電子報</p>
            <p className={classNames('text-base')}>
              將每月定期發送美國第一手趨勢報告
              <span className={classNames('hidden invisible', 'sm:inline-block sm:visible')}>，</span>
              <br className={classNames('sm:hidden sm:invisible')} />
              讓您即時掌握最新房產動態！
            </p>
          </div>
          <div
            className={classNames(
              'sm:flex sm:items-center sm:justify-end',
              'space-y-4 sm:space-y-0',
              'sm:space-x-3',
              'sm:flex-1'
            )}
          >
            <div
              className={classNames(
                'sm:flex-1',
                'py-2 px-4',
                'sm:w-96',
                errors.email ? 'border-red  border' : 'border-black border-[0.5px]'
              )}
            >
              <input
                type={'email'}
                placeholder={'Your Email Address'}
                className={classNames(
                  'w-full h-6',
                  'border-none outline-none',
                  'bg-transparent',
                  'placeholder:text-gray300'
                )}
                {...register('email', {
                  validate: emailRequired,
                })}
              />
            </div>
            <button className={classNames('py-2 px-8 text-white bg-gold')}>訂閱</button>
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};
